import React, { useReducer, useContext, useEffect, useRef } from "react";
import reservasContext from "./reservasContext";
import reservasReducer from "./reservasReducer";
import { SocketContext } from "../socket/socket";
import authContext from "../authentication/authContex";
import Swal from "sweetalert2";

import {
  SET_RESERVAS,
  SET_MIS_RESERVAS,
  SET_SLOTS_DISPONIBLES,
  SET_CANCHAS_DISPONIBILIDAD,
  SET_BLOQUEOS,
  SET_CANCHA_SELECCIONADA,
  SET_FECHA_SELECCIONADA,
  SET_SOCIOS,
  SET_LOADING_SOCIOS,
  SET_LOADING,
  SET_ERROR,
  ADD_RESERVA,
  UPDATE_RESERVA,
  DELETE_RESERVA,
} from "./reservasActions";

import { getUsersAsOptionsByUserType } from "../../api/user";

import {
  getSlotsDisponibles,
  getSlotsTodasCanchas,
  getMisReservas,
  getAllReservas,
  getReservaById,
  createReserva,
  updateReserva as updateReservaAPI,
  cancelarReserva as cancelarReservaAPI,
  suspenderReserva as suspenderReservaAPI,
  reactivarReserva as reactivarReservaAPI,
  deleteReserva as deleteReservaAPI,
  getAllDisponibilidades,
  getDisponibilidadByActivoId,
  createDisponibilidad,
  createDisponibilidadDefault,
  updateDisponibilidad as updateDisponibilidadAPI,
  getAllBloqueos,
  getBloqueosByActivoAndFecha,
  createBloqueo,
  updateBloqueo as updateBloqueoAPI,
  cancelarBloqueo as cancelarBloqueoAPI,
  deleteBloqueo as deleteBloqueoAPI,
} from "../../api/reservas";

const ReservasState = (props) => {
  const server = useContext(SocketContext);
  const aContext = useContext(authContext);
  const { usuario, verificarTenant } = aContext;

  const initialState = {
    reservas: null,
    misReservas: null,
    slotsDisponibles: null,
    canchasDisponibilidad: null,
    bloqueos: null,
    canchaSeleccionada: null,
    fechaSeleccionada: new Date(),
    socios: [],
    loadingSocios: false,
    loading: false,
    error: null,
  };

  const [state, dispatch] = useReducer(reservasReducer, initialState);

  // ============ SOCIOS ============

  /*
    Padron de socios usado para poblar los selects de jugadores en los modales de reserva.
    Se carga una sola vez al iniciar sesion y se refresca cuando el backend avisa por socket
    ('sociosListChanged'). Los modales solo leen state.socios: no disparan peticiones.

    sociosEnVueloRef deduplica llamadas concurrentes (por ejemplo, si el evento de socket
    llega mientras la carga inicial todavia esta en curso).
  */
  const sociosEnVueloRef = useRef(null);

  const obtenerSocios = async () => {
    if (sociosEnVueloRef.current) return sociosEnVueloRef.current;

    const peticion = (async () => {
      try {
        dispatch({ type: SET_LOADING_SOCIOS, payload: true });
        const response = await getUsersAsOptionsByUserType("SOCIO");
        const data = response?.data?.data ?? [];

        //Formato que espera react-select en los modales.
        const sociosFormateados = data.map((socio) => ({
          value: socio._id,
          label: socio.fullName || socio.userName,
          userName: socio.userName,
          fullName: socio.fullName,
          shortName: socio.shortName || "",
        }));

        dispatch({ type: SET_SOCIOS, payload: sociosFormateados });
        return sociosFormateados;
      } catch (error) {
        console.error("Error al cargar socios:", error);
        dispatch({ type: SET_LOADING_SOCIOS, payload: false });
        return [];
      } finally {
        sociosEnVueloRef.current = null;
      }
    })();

    sociosEnVueloRef.current = peticion;
    return peticion;
  };

  useEffect(() => {
    if (usuario != null) obtenerSocios();
  }, [usuario]);

  /*
    El backend emite 'sociosListChanged' al crear, modificar o borrar un usuario que
    afecte al padron de socios. El emit es global a todos los sockets conectados, asi que
    hay que filtrar por tenant como hacen el resto de los contextos.

    Tambien se refresca al reconectar: si el socket estuvo caido (pestana en segundo plano,
    corte de red) el evento se pierde y la lista quedaria desactualizada hasta el proximo login.
  */
  useEffect(() => {
    if (!server) return;

    const handleSociosListChanged = ({ tenantId }) => {
      if (verificarTenant(tenantId)) obtenerSocios();
    };

    const handleConnect = () => {
      if (usuario != null) obtenerSocios();
    };

    server.on("sociosListChanged", handleSociosListChanged);
    server.on("connect", handleConnect);
    return () => {
      server.off("sociosListChanged", handleSociosListChanged);
      server.off("connect", handleConnect);
    };
  }, [server, usuario]);

  // ============ SLOTS DISPONIBLES ============

  const obtenerSlotsDisponibles = async (activoID, fecha) => {
    try {
      dispatch({ type: SET_LOADING, payload: true });
      const response = await getSlotsDisponibles(activoID, fecha);
      dispatch({
        type: SET_SLOTS_DISPONIBLES,
        payload: response.data.data,
      });
      return response.data.data;
    } catch (error) {
      console.error("Error al obtener slots disponibles:", error);
      dispatch({
        type: SET_ERROR,
        payload: error.response?.data?.message || "Error al cargar disponibilidad",
      });
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Error al cargar disponibilidad",
      });
    }
  };

  const obtenerSlotsTodasCanchas = async (activoIDs, fecha) => {
    try {
      dispatch({ type: SET_LOADING, payload: true });
      const response = await getSlotsTodasCanchas(activoIDs, fecha);
      return response.data.data;
    } catch (error) {
      console.error("Error al obtener slots de todas las canchas:", error);
      dispatch({
        type: SET_ERROR,
        payload: error.response?.data?.message || "Error al cargar disponibilidad",
      });
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Error al cargar disponibilidad",
      });
      return {};
    } finally {
      dispatch({ type: SET_LOADING, payload: false });
    }
  };

  // ============ RESERVAS ============

  const obtenerMisReservas = async (soloFuturas = true, estado = null, incluirComoJugador = false) => {
    try {
      dispatch({ type: SET_LOADING, payload: true });
      const response = await getMisReservas(soloFuturas, estado, incluirComoJugador);
      dispatch({
        type: SET_MIS_RESERVAS,
        payload: response.data.data,
      });
      return response.data.data;
    } catch (error) {
      console.error("Error al obtener mis reservas:", error);
      dispatch({
        type: SET_ERROR,
        payload: error.response?.data?.message || "Error al cargar reservas",
      });
    }
  };

  const obtenerTodasReservas = async (fechaDesde, fechaHasta, activoID = null) => {
    try {
      dispatch({ type: SET_LOADING, payload: true });
      const response = await getAllReservas(fechaDesde, fechaHasta, activoID);
      dispatch({
        type: SET_RESERVAS,
        payload: response.data.data,
      });
      return response.data.data;
    } catch (error) {
      console.error("Error al obtener reservas:", error);
      dispatch({
        type: SET_ERROR,
        payload: error.response?.data?.message || "Error al cargar reservas",
      });
    }
  };

  const crearReserva = async (reservaData) => {
    try {
      dispatch({ type: SET_LOADING, payload: true });
      const response = await createReserva(reservaData);
      dispatch({
        type: ADD_RESERVA,
        payload: response.data.data,
      });
      Swal.fire({
        icon: "success",
        title: "Reserva creada",
        text: "La reserva se creó exitosamente",
        timer: 2000,
        didClose: () => {
          // Limpiar padding-right que Bootstrap Modal puede haber dejado
          document.body.style.paddingRight = '';
          document.body.style.overflow = '';
        }
      });
      return response.data.data;
    } catch (error) {
      console.error("Error al crear reserva:", error);
      dispatch({
        type: SET_ERROR,
        payload: error.response?.data?.message || "Error al crear reserva",
      });
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Error al crear reserva",
        didClose: () => {
          // Limpiar padding-right que Bootstrap Modal puede haber dejado
          document.body.style.paddingRight = '';
          document.body.style.overflow = '';
        }
      });
      throw error;
    }
  };

  const actualizarReserva = async (id, reservaData) => {
    try {
      dispatch({ type: SET_LOADING, payload: true });
      const response = await updateReservaAPI(id, reservaData);
      dispatch({
        type: UPDATE_RESERVA,
        payload: response.data.data,
      });
      Swal.fire({
        icon: "success",
        title: "Reserva actualizada",
        timer: 2000,
      });
      return response.data.data;
    } catch (error) {
      console.error("Error al actualizar reserva:", error);
      dispatch({
        type: SET_ERROR,
        payload: error.response?.data?.message || "Error al actualizar reserva",
      });
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Error al actualizar reserva",
      });
    }
  };

  const cancelarReserva = async (id, motivo) => {
    try {
      dispatch({ type: SET_LOADING, payload: true });
      const response = await cancelarReservaAPI(id, motivo);
      dispatch({
        type: UPDATE_RESERVA,
        payload: response.data.data,
      });
      Swal.fire({
        icon: "success",
        title: "Reserva cancelada",
        text: "La reserva se canceló exitosamente",
        timer: 2000,
      });
      return response.data.data;
    } catch (error) {
      console.error("Error al cancelar reserva:", error);
      dispatch({
        type: SET_ERROR,
        payload: error.response?.data?.message || "Error al cancelar reserva",
      });
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Error al cancelar reserva",
      });
    }
  };

  const suspenderReserva = async (id, motivo) => {
    try {
      dispatch({ type: SET_LOADING, payload: true });
      const response = await suspenderReservaAPI(id, motivo);
      dispatch({
        type: UPDATE_RESERVA,
        payload: response.data.data,
      });
      Swal.fire({
        icon: "success",
        title: "Reserva suspendida",
        text: "La reserva se suspendió exitosamente",
        timer: 2000,
      });
      return response.data.data;
    } catch (error) {
      console.error("Error al suspender reserva:", error);
      dispatch({
        type: SET_ERROR,
        payload: error.response?.data?.message || "Error al suspender reserva",
      });
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Error al suspender reserva",
      });
    }
  };

  const reactivarReserva = async (id) => {
    try {
      dispatch({ type: SET_LOADING, payload: true });
      const response = await reactivarReservaAPI(id);
      dispatch({
        type: UPDATE_RESERVA,
        payload: response.data.data,
      });
      Swal.fire({
        icon: "success",
        title: "Reserva reactivada",
        text: "La reserva se reactivó exitosamente",
        timer: 2000,
      });
      return response.data.data;
    } catch (error) {
      console.error("Error al reactivar reserva:", error);
      dispatch({
        type: SET_ERROR,
        payload: error.response?.data?.message || "Error al reactivar reserva",
      });
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Error al reactivar reserva",
      });
    }
  };

  const eliminarReserva = async (id) => {
    try {
      dispatch({ type: SET_LOADING, payload: true });
      await deleteReservaAPI(id);
      dispatch({
        type: DELETE_RESERVA,
        payload: id,
      });
      Swal.fire({
        icon: "success",
        title: "Reserva eliminada",
        timer: 2000,
      });
    } catch (error) {
      console.error("Error al eliminar reserva:", error);
      dispatch({
        type: SET_ERROR,
        payload: error.response?.data?.message || "Error al eliminar reserva",
      });
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Error al eliminar reserva",
      });
    }
  };

  // ============ DISPONIBILIDAD ============

  const obtenerDisponibilidades = async () => {
    try {
      dispatch({ type: SET_LOADING, payload: true });
      const response = await getAllDisponibilidades();
      dispatch({
        type: SET_CANCHAS_DISPONIBILIDAD,
        payload: response.data.data,
      });
      return response.data.data;
    } catch (error) {
      console.error("Error al obtener disponibilidades:", error);
      dispatch({
        type: SET_ERROR,
        payload: error.response?.data?.message || "Error al cargar configuraciones",
      });
    }
  };

  const obtenerDisponibilidadDeCancha = async (activoID) => {
    try {
      dispatch({ type: SET_LOADING, payload: true });
      const response = await getDisponibilidadByActivoId(activoID);
      dispatch({ type: SET_LOADING, payload: false });
      return response.data.data;
    } catch (error) {
      console.error("Error al obtener disponibilidad:", error);
      dispatch({ type: SET_LOADING, payload: false });
      return null;
    }
  };

  const crearDisponibilidadDefault = async (activoID, codActivo) => {
    try {
      dispatch({ type: SET_LOADING, payload: true });
      const response = await createDisponibilidadDefault(activoID, codActivo);
      dispatch({ type: SET_LOADING, payload: false });
      Swal.fire({
        icon: "success",
        title: "Configuración creada",
        timer: 2000,
      });
      return response.data.data;
    } catch (error) {
      console.error("Error al crear configuración:", error);
      dispatch({ type: SET_LOADING, payload: false });
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Error al crear configuración",
      });
    }
  };

  // ============ BLOQUEOS ============

  const obtenerBloqueos = async (estado = null) => {
    try {
      dispatch({ type: SET_LOADING, payload: true });
      const response = await getAllBloqueos(estado);
      dispatch({
        type: SET_BLOQUEOS,
        payload: response.data.data,
      });
      return response.data.data;
    } catch (error) {
      console.error("Error al obtener bloqueos:", error);
      dispatch({
        type: SET_ERROR,
        payload: error.response?.data?.message || "Error al cargar bloqueos",
      });
    }
  };

  const obtenerBloqueosPorCancha = async (activoID, fecha) => {
    try {
      const response = await getBloqueosByActivoAndFecha(activoID, fecha);
      return response.data.data;
    } catch (error) {
      console.error("Error al obtener bloqueos:", error);
      return [];
    }
  };

  const crearBloqueo = async (bloqueoData) => {
    try {
      dispatch({ type: SET_LOADING, payload: true });
      const response = await createBloqueo(bloqueoData);
      dispatch({ type: SET_LOADING, payload: false });
      Swal.fire({
        icon: "success",
        title: "Bloqueo creado",
        timer: 2000,
      });
      return response.data.data;
    } catch (error) {
      console.error("Error al crear bloqueo:", error);
      dispatch({ type: SET_LOADING, payload: false });
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Error al crear bloqueo",
      });
    }
  };

  // ============ SETTERS ============

  const setCanchaSeleccionada = (cancha) => {
    dispatch({
      type: SET_CANCHA_SELECCIONADA,
      payload: cancha,
    });
  };

  const setFechaSeleccionada = (fecha) => {
    dispatch({
      type: SET_FECHA_SELECCIONADA,
      payload: fecha,
    });
  };

  return (
    <reservasContext.Provider
      value={{
        reservas: state.reservas,
        misReservas: state.misReservas,
        slotsDisponibles: state.slotsDisponibles,
        canchasDisponibilidad: state.canchasDisponibilidad,
        bloqueos: state.bloqueos,
        canchaSeleccionada: state.canchaSeleccionada,
        fechaSeleccionada: state.fechaSeleccionada,
        socios: state.socios,
        loadingSocios: state.loadingSocios,
        loading: state.loading,
        error: state.error,
        obtenerSlotsDisponibles,
        obtenerSlotsTodasCanchas,
        obtenerMisReservas,
        obtenerTodasReservas,
        crearReserva,
        actualizarReserva,
        cancelarReserva,
        suspenderReserva,
        reactivarReserva,
        eliminarReserva,
        obtenerDisponibilidades,
        obtenerDisponibilidadDeCancha,
        crearDisponibilidadDefault,
        obtenerBloqueos,
        obtenerBloqueosPorCancha,
        crearBloqueo,
        setCanchaSeleccionada,
        setFechaSeleccionada,
      }}
    >
      {props.children}
    </reservasContext.Provider>
  );
};

export default ReservasState;
